import { ArrowRightIcon, ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import { memo, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "~/lib/utils";
import {
  PROMPT_EXAMPLE_CATEGORIES,
  PROMPT_EXAMPLES,
  PROMPT_EXAMPLES_COLLAPSED_COUNT,
  shufflePromptExamples,
  type PromptExample,
} from "./promptExampleCatalog";

interface PromptExamplesProps {
  onPick: (text: string) => void;
}

export const PromptExamples = memo(function PromptExamples({ onPick }: PromptExamplesProps) {
  const { t } = useTranslation();
  const [examples] = useState(() => shufflePromptExamples(PROMPT_EXAMPLES));
  const [expanded, setExpanded] = useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);
  const visibleExamples = expanded ? examples : examples.slice(0, PROMPT_EXAMPLES_COLLAPSED_COUNT);

  // Expanding keeps the scroll offset the collapsed fade left behind, which lands the
  // reader mid-list on a page they have never seen. Pin it back to the first example.
  useLayoutEffect(() => {
    if (expanded) {
      listRef.current?.scrollTo({ top: 0 });
    }
  }, [expanded]);

  return (
    <div
      data-prompt-examples="true"
      className="flex h-full min-h-0 flex-col overflow-y-auto pl-[calc(env(safe-area-inset-left)+0.75rem)] pr-[calc(env(safe-area-inset-right)+0.75rem)] sm:pl-[calc(env(safe-area-inset-left)+1.25rem)] sm:pr-[calc(env(safe-area-inset-right)+1.25rem)]"
    >
      <div className="mx-auto mt-auto flex w-full min-w-0 max-w-208 flex-col pb-6 sm:pb-8">
        <div
          ref={listRef}
          className={cn(
            "flex min-h-0 flex-col",
            expanded ? "max-h-[min(44vh,420px)] overflow-y-auto overscroll-y-contain" : null,
          )}
        >
          {visibleExamples.map((example) => (
            <PromptExampleRow key={example.text} example={example} onPick={onPick} />
          ))}
          <div
            className={cn(
              "pointer-events-none sticky bottom-0 shrink-0 bg-linear-to-b from-background/0",
              expanded ? "-mt-14 h-14 to-background" : "-mt-16 h-16 to-background/90",
            )}
          />
        </div>
        <div className="flex justify-center pt-1">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[13px] text-muted-foreground/80 hover:text-foreground"
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? t("common/showLess_to2zoz") : t("common/showMore_yjpez8")}
            {expanded ? (
              <ChevronUpIcon className="size-3.5" />
            ) : (
              <ChevronDownIcon className="size-3.5" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
});

const PromptExampleRow = memo(function PromptExampleRow({
  example,
  onPick,
}: {
  example: PromptExample;
  onPick: (text: string) => void;
}) {
  const category = PROMPT_EXAMPLE_CATEGORIES.find((entry) => entry.id === example.categoryId);
  const Icon = category?.icon;
  return (
    <button
      type="button"
      title={category?.label}
      className="group flex w-full min-w-0 shrink-0 items-center gap-3 rounded-lg px-3.5 py-2 text-left text-sm text-foreground/80 hover:bg-accent"
      onClick={() => onPick(example.text)}
    >
      {Icon ? (
        <Icon className="size-4 shrink-0 text-muted-foreground group-hover:text-foreground" />
      ) : null}
      <span className="min-w-0 flex-1 truncate">{example.text}</span>
      <ArrowRightIcon className="size-4 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100" />
    </button>
  );
});
